import {InPageAlert, TextLink} from '@snsw-gel/react';
import React from 'react';
import {TollAccountFailure} from '../../services/api-services';
import config from '../../config/config';
import {PhoneElement} from '../../utils/elements';

interface ApiErrorAlertProps {
    readonly errorReason: TollAccountFailure;
}

const ApiErrorAlert: React.FC<ApiErrorAlertProps> = ({errorReason}) => {
    const [title, description] = errorTitleAndDescription(errorReason);

    return <InPageAlert variant='error' title={title} role='alert'>
        {description}
    </InPageAlert>;
};

function errorTitleAndDescription(errorReason: TollAccountFailure): [string, React.JSX.Element] {
    let title: string;
    let description: React.JSX.Element;
    switch (errorReason) {
        case TollAccountFailure.tollAccountDetailsInvalid:
        case TollAccountFailure.tspApiTollAccountNotFound:
            title = 'Update your details';
            description = <>
                <p>We need more details from you to continue.</p>
                <p>Select 'Unlink and relink'. This will prompt you to link your toll account so you can update your details.</p>
            </>;
            break;
        case TollAccountFailure.tomeeTollAccountNotFound:
            title = 'Unable to link toll account';
            description = <>
                <p>The toll account details you entered don't match the details provided by the toll provider.</p>
                <p>Check if the information you entered is correct and matches your toll provider's records. If you have recently updated your details with your toll provider, please wait at least 3 days before trying again.</p>
                <p>If you need help, call {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountCannotBeVerified:
            title = 'Unable to link toll account';
            description = <>
                <p>The toll account details you have provided don't match the details provided by the toll provider.</p>
                <p>To resolve this, contact your toll provider.</p>
                <p>If you know that your details have changed, you can also remove the toll account that is currently linked,
                    and link a different toll account using the link in this page.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountProductTypeInvalid:
            title = 'We cannot link this toll account';
            description = <>
                <p>The toll account provided is associated with an invalid toll product. Casual toll products (such as LinktGO) or special passes (for example, the ERider and eMU passes) are not eligible for Toll Relief. Check the eligibility criteria.</p>
                <p>If you need help, call {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountInactive:
            title = 'Unable to continue';
            description = <>
                <p>Your toll account has a status of suspended or inactive.</p>
                <p>To resolve this, contact your toll provider.</p>
                <p>If you know that your details have changed, you can also remove the toll account that is currently linked,
                    and link a different toll account using the link in this page.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountAssociatedToABN:
            title = 'Unable to link toll account';
            description = <>
                <p>Your toll account is associated with an ABN.</p>
                <p>Personal toll accounts associated with an ABN are not eligible for Toll Relief.
                    Check the <TextLink isExternal href={config.links.personalEligibilityLink}>eligibility criteria
                    </TextLink>.</p>
                <p>If you know that your details have changed, you can also remove the toll account that is currently linked,
                    and link a different toll account using the link in this page.</p>
                <p>If you need help, call {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.tollAccountOutsideNSW:
            title = 'Unable to link toll account';
            description = <>
                <p>We cannot find a toll account linked to the details you've provided.</p>
                <p>Check the details from your toll provider are correct, and try again.</p>
                <p>If you know that your details have changed, you can also remove the toll account that is currently linked,
                    and link a different toll account using the link in this page.</p>
            </>;
            break;
        case TollAccountFailure.myAccountOutsideNSW:
            title = 'Unable to link account outside of NSW';
            description = <>
                <p>Your MyServiceNSW account address details indicates you are not living in NSW. The Toll Relief scheme
                    is available for{' '}
                    <TextLink isExternal href={config.links.personalEligibilityLink}
                    >eligible customers
                    </TextLink>
                    {' '}
                    living in NSW.
                </p>
                <p>If you think you’re eligible, please call us on {PhoneElement}.</p>
            </>;
            break;
        case TollAccountFailure.serviceProviderError:
            title = 'Unable to process your request';
            description = <>
                <p>We could not process your request due to a technical issue on our end.
                </p>
                <p>
                    Please try again. If the issue keeps happening, call us on {PhoneElement}.
                </p>
            </>;
            break;
        case TollAccountFailure.etollApiNotAvailable:
        case TollAccountFailure.linktApiNotAvailable:
            title = 'Service interruption';
            description =
                <p>It looks like there may be an interruption to the service, which may affect the processing of your request.
                    We recommend returning here at later time and trying again.
                </p>;
            break;
        case TollAccountFailure.tollAccountAlreadyLinked:
            title = 'Toll account already linked';
            description = <>
                <p>The details you've provided are already linked to another MyService NSW account.</p>
                <p>Only one toll account can be linked. If you need any assistance, contact us on {PhoneElement}</p>
            </>;
            break;
        default:
            console.error(`toll account failure ${errorReason} not recognized`);
            title = 'Unable to link this toll account';
            description = <></>;
    }

    return [title, description];
}

export default ApiErrorAlert;
